import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useRef } from "react";
import styled from "styled-components";

import { DragBounds, DragModifications, PogAtom, Scaleable } from "../../store/types";
import { resolveDraggedItems } from "../../store/utils";
import { Draggable } from "../Draggable/Draggable";
import { Dropper } from "../Item/Dropper";
import { DraggedItemsPayload } from "../Item/store/types";
import { Item } from "../Item/Item";
import { onDropOverToShelf } from "../Item/store/utils";
import { Overflowing } from "./Overflowing";
import { ShelfBottom } from "./ShelfBottom";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import {
  planogramHoveredShelfAtom,
  planogramSelectedShelfAtom,
  showShelfOverflowAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Flex } from "src/elements";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { usePogAtomActions } from "../../store/actions";
import { calculateShelfSelectionUpdate } from "../../store/utils";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { getMergedMerchandisableWidth } from "@CommonUtils/pog/shelfMerchandising";
import { NonMerchandisableArea } from "./NonMerchandisableArea";

const ShelfInner = styled(Flex)<{ height: number; scaleY: number }>`
  width: 100%;
  height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: absolute;
  bottom: 0;
  left: 0;
`;

const Items = styled(Flex)<{
  $merchandisableWidth: number;
  $scaleX: number;
  isSideView?: boolean;
  align?: "left" | "right" | "between" | "around";
}>`
  width: ${({ $merchandisableWidth, $scaleX }) => `calc(${$merchandisableWidth}px * ${$scaleX})`};
  height: 0;
  position: relative;
  align-items: flex-end;
  justify-content: ${({ isSideView, align }) => {
    if (isSideView) return "flex-end";
    if (align === "right") return "flex-end";
    if (align === "left") return "flex-start";
    if (align === "around") return "space-around";
    return "space-between";
  }};
  flex-grow: 1;

  // First element is dropper, ignore that
  // Second element is always first item in the shelf.
  > *:nth-of-type(2) {
    flex-grow: 0;
  }

  ${({ align }) =>
    (align === "left" || align === "right") &&
    `
    > * {
      flex-grow: 0;
    }
  `}
`;

const ItemsAnchor = styled(Flex)`
  position: absolute;
`;

const ShelfBottoms = styled(Flex)``;

interface Props extends Scaleable {
  currentStoreCode?: string;
  bay: PlanogramBay;
  shelves: PlanogramShelf[];
  bounds: DragBounds;
  isEditable: boolean;
  isPog: boolean;
  highlightedItems: string[];
  pogAtom: PogAtom;
  setIsModified: (modified: boolean) => void;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  pdfMode: boolean;
  customItemLabelOnly?: boolean;
  customLabelRule?: (item: PlanogramItem | UnrangedPlanogramItem) => string | null | undefined;
  pdfShelfLabelBayNo?: number;
  isSideView?: boolean;
  bayShift?: number;
}

export const MergedShelf = ({
  currentStoreCode,
  bay,
  shelves,
  bounds,
  isEditable,
  isPog,
  highlightedItems,
  scaleX,
  scaleY,
  pogAtom,
  setIsModified,
  availableStores,
  pdfMode,
  customItemLabelOnly,
  customLabelRule,
  pdfShelfLabelBayNo,
  isSideView,
  bayShift = 0,
}: Props) => {
  const pog = useAtomValue(pogAtom);
  const { addItemsToShelf, duplicateItemsToShelf, moveShelf } = usePogAtomActions(pogAtom);

  const setSelectedShelf = useSetAtom(planogramSelectedShelfAtom);
  const [selectedShelfIds, setSelectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const { setTab } = usePlanogramPanel();
  const hoveredShelf = useAtomValue(planogramHoveredShelfAtom);
  const showShelfOverflow = useAtomValue(showShelfOverflowAtom);

  const firstShelf = shelves[0];
  const lastShelf = shelves[shelves.length - 1];
  const shelfHeight = firstShelf.getHeight();
  const shelfThickness = firstShelf.thickness;
  const merchandisableWidth = getMergedMerchandisableWidth(shelves);
  const isSelected = shelves.some((shelf) => selectedShelfIds.includes(shelf.uniqueId));

  const items: PlanogramItem[] = shelves.reduce(
    (items, shelf) => [...items, ...shelf.items],
    [] as PlanogramItem[],
  );

  const refItems = useRef<HTMLDivElement>(null);

  const onModify = ({ yBottom }: DragModifications) => {
    setIsModified(true);
    moveShelf(firstShelf, firstShelf.getNewNotchNo(yBottom));
  };

  const onDropOver = () => {
    setIsModified(true);

    onDropOverToShelf({
      refItems,
      onDrop,
    });
  };

  const onDrop = ({ itemIds, mode }: DraggedItemsPayload) => {
    setIsModified(true);

    if (mode === "copy") {
      duplicateItemsToShelf(itemIds, firstShelf);
      return;
    }

    if (!pog) {
      return;
    }

    const draggedItems = resolveDraggedItems(pog, itemIds);

    if (draggedItems.length > 0) {
      addItemsToShelf(draggedItems, firstShelf);
    }
  };

  const setSelected = (event: React.PointerEvent) => {
    const shelf = hoveredShelf || firstShelf;
    const { nextSelectedShelfIds, nextSelectedShelf, nextTab } = calculateShelfSelectionUpdate(
      pog,
      selectedShelfIds,
      shelf,
      event,
    );

    setSelectedShelfIds(nextSelectedShelfIds);
    setSelectedShelf(nextSelectedShelf);
    setTab(nextTab);
  };

  return (
    <Draggable
      uniqueId={firstShelf.uniqueId}
      xLeft={(isSideView ? bounds.xLeft : firstShelf.getXLeft()) + bayShift}
      xRight={(isSideView ? bounds.xRight : lastShelf.getXRight()) + bayShift}
      yBottom={isSideView ? bounds.yBottom : firstShelf.getYBottom()}
      yTop={isSideView ? bounds.yTop : firstShelf.getYBottom()}
      scaleX={scaleX}
      scaleY={scaleY}
      bounds={{
        ...bounds,
        xLeft: bounds.xLeft + bayShift,
        xRight: bounds.xRight + bayShift,
      }}
      snappables={[]}
      isDraggable={isEditable}
      direction="vertical"
      moveBy={bay.notchSpacing}
      isSideView={isSideView}
      onModify={onModify}
      onClick={setSelected}
    >
      <ShelfInner height={shelfHeight} scaleY={scaleY}>
        {isPog && items.length <= 1 && (
          <Dropper
            side="left"
            width={`${merchandisableWidth * scaleX}px`}
            height={`calc(${shelfHeight - shelfThickness}px * ${scaleY})`}
            bottom={`calc(${shelfThickness}px * ${scaleY})`}
            onDropOver={onDropOver}
            onDrop={onDrop}
          />
        )}

        {!pdfMode && showShelfOverflow && firstShelf.isOverflowing() && (
          <Overflowing shelf={firstShelf} scaleY={scaleY} />
        )}

        <Items
          ref={refItems}
          $merchandisableWidth={isSideView ? bounds.xRight - bounds.xLeft : merchandisableWidth}
          $scaleX={scaleX}
          isSideView={isSideView}
          align={firstShelf.align}
        >
          <ItemsAnchor />

          {items.map((item, i) => (
            <React.Fragment key={i}>
              <Item
                currentStoreCode={currentStoreCode}
                item={item}
                scaleX={scaleX}
                scaleY={scaleY}
                isEditable={isEditable}
                isHighlighted={highlightedItems.includes(item.uniqueId)}
                isScrolledToView={
                  highlightedItems.length === 1 && highlightedItems.includes(item.uniqueId)
                }
                pogAtom={pogAtom}
                setIsModified={setIsModified}
                availableStores={availableStores}
                pdfMode={pdfMode}
                customItemLabelOnly={customItemLabelOnly}
                customLabelRule={customLabelRule}
              />
            </React.Fragment>
          ))}
        </Items>

        <ShelfBottoms>
          {shelves.map((shelf, i) => (
            <ShelfBottom
              key={i}
              shelf={shelf}
              scaleX={scaleX}
              scaleY={scaleY}
              isSelected={isSelected}
              pogAtom={pogAtom}
              showShelfName={pdfMode}
              shelfLabelBayNo={pdfShelfLabelBayNo}
              setIsModified={setIsModified}
            />
          ))}
        </ShelfBottoms>

        {!isSideView && <NonMerchandisableArea scaleX={scaleX} scaleY={scaleY} shelves={shelves} />}
      </ShelfInner>
    </Draggable>
  );
};
